import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { apiClient, API_PATHS, parseHttpError, isEmbedded } from '@cyberint/common-utils';
import { useSnackbars, Modal, Themes } from '@cyberint/dsc';
import { t } from '../../i18n';
import { Privacy } from './Privacy';
import { Terms } from './Terms';
import * as S from './styled';

const SIZE = { width: '80vw', height: '80vh' };

const signPrivacyPolicy = () => apiClient.defaultClient.post(`${API_PATHS.API_BASE_URL}/user/sign_privacy_policy`, {});

const TabContent = ({ content, title }) => (
  <S.TabWrapper>
    {title}
    <S.ContentContainer>{content}</S.ContentContainer>
  </S.TabWrapper>
);

TabContent.propTypes = {
  content: PropTypes.node,
  title: PropTypes.node
};

const Content = () => (
  <S.ContentWrapper>
    <TabContent content={<Privacy />} title={<S.Title>Privacy Policy</S.Title>} />
    <TabContent
      content={<Terms />}
      title={
        <S.Title>
          Terms of Service – Cloud Services; Infinity Portal
          <br />
          Cloud End-user License Agreement
        </S.Title>
      }
    />
  </S.ContentWrapper>
);

/**
 * Hoisted from apps/ui/src/components/root/approvals so the shell can run it too.
 * `user` and `onCancel` are injected: the host app owns its sign-out flow (legacy
 * dispatches redux signOut + useOnLogout; the shell uses its useSignOut hook).
 * Opens when the authenticated user has no signed_policy_date; Cancel signs out
 * (hidden in embedded mode, where there is no login page to fall back to).
 */
export const TermsAndConditionsBase = ({ user, onCancel }) => {
  const { errorSnackbar } = useSnackbars();

  const [isOpen, setOpen] = useState(false);
  const [isSaving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) {
      setOpen(false);
      return;
    }
    /** For old users, migration will put value.
     * For new users, the value will be null (or falsy) - meaning open confirmation.
     * For approved policy, there will be value */
    setOpen(!user.signed_policy_date);
  }, [user]);

  const setConfirmed = useCallback(async () => {
    setSaving(true);

    try {
      await signPrivacyPolicy();
    } catch (e) {
      errorSnackbar(parseHttpError(e));
    }

    setSaving(false);
    setOpen(false);
  }, []);

  return (
    <Modal
      mode={Themes.LIGHT}
      size={SIZE}
      isOpen={isOpen}
      title={t('termsAndConditions.title')}
      content={<Content />}
      disabled={isSaving}
      isLoading={isSaving}
      okText={t('termsAndConditions.confirm')}
      okTestId="button_tc_ok"
      onOk={setConfirmed}
      cancelText={isEmbedded ? null : t('termsAndConditions.cancelAndLogout')}
      onClose={isEmbedded ? null : onCancel}
    />
  );
};

TermsAndConditionsBase.propTypes = {
  user: PropTypes.object,
  onCancel: PropTypes.func
};
